'use no memo'

import { flexRender, type Row, type Table as TableType } from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { cn } from '~/lib/utils'

interface DataTableProps<TData> {
  table: TableType<TData>
}

type AppTableVariant = 'default' | 'green'

type AppTableColumnSizing = 'all' | 'only-explicit' | 'none'

interface AppTableProps {
  variant?: AppTableVariant
}

export const AppTable = ({ children }: React.PropsWithChildren<AppTableProps>) => (
  <div className="flex flex-col gap-4 rounded-3xl border bg-background p-4">{children}</div>
)

export const AppTableData = <TData,>({
  table,
  variant = 'default',
  columnSizing = 'all',
  tableClassName,
  wrapperClassName,
  rowClassName,
  onRowClick,
  isRowClickable = () => true,
}: DataTableProps<TData> &
  AppTableProps & {
    columnSizing?: AppTableColumnSizing
    tableClassName?: string
    /** Classes supplémentaires du cadre du tableau (coins, bordure). */
    wrapperClassName?: string
    /** Classes supplémentaires d'une ligne (ex. surface d'alerte d'une ligne à traiter). */
    rowClassName?: (row: Row<TData>) => string | undefined
    /** Rend chaque ligne cliquable (curseur + clavier) — pour ouvrir un panneau de détail. */
    onRowClick?: (row: Row<TData>) => void
    /** Lignes que `onRowClick` concerne (toutes par défaut) : les autres restent inertes. */
    isRowClickable?: (row: Row<TData>) => boolean
  }): React.JSX.Element => {
  const headerClassName = variant === 'green' ? 'bg-[#F6F8F5]' : undefined
  const headerCellClassName = variant === 'green' ? 'py-4' : undefined

  interface SizedColumn {
    getSize: () => number
    columnDef: {
      size?: unknown
    }
  }

  const getCellWidthStyle = (
    column: SizedColumn,
  ): { width: number; minWidth: number; maxWidth: number } | undefined => {
    if (columnSizing === 'none') {
      return undefined
    }

    const definedSize = column.columnDef.size
    const hasDefinedSize = typeof definedSize === 'number'
    if (columnSizing === 'only-explicit' && !hasDefinedSize) {
      return undefined
    }

    const size = column.getSize()
    return {
      width: size,
      minWidth: size,
      maxWidth: size,
    }
  }

  return (
    <div className={cn('overflow-hidden rounded-2xl border', wrapperClassName)}>
      <Table className={cn('table-fixed', tableClassName)}>
        <TableHeader className={headerClassName}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn(headerCellClassName)}
                  style={getCellWidthStyle(header.column)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const onClick = onRowClick && isRowClickable(row) ? onRowClick : undefined
              return (
                <TableRow
                  data-state={row.getIsSelected() && 'selected'}
                  key={row.id}
                  className={cn(onClick && 'cursor-pointer', rowClassName?.(row))}
                  onClick={onClick ? () => onClick(row) : undefined}
                  onKeyDown={
                    onClick
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onClick(row)
                          }
                        }
                      : undefined
                  }
                  tabIndex={onClick ? 0 : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} style={getCellWidthStyle(cell.column)}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell className="h-24 text-center" colSpan={table.getAllColumns().length}>
                Aucun résultat.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
